import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/** @typedef {import('./FormDesigner.jsx').DesignerFieldStateObj} DesignerFieldStateObj */
/** @typedef {import('../enums.js').FormFieldTypeObj} FormFieldTypeObj */

/**
 * @typedef {object} FormDesignerFieldProps
 * @property {DesignerFieldStateObj} field - Champ de la ligne
 * @property {number} index - Index du champ dans le formulaire
 * @property {Array<FormFieldTypeObj>} types - Types de champ disponibles
 * @property {boolean} selected - Si le champ est en cours d'édition
 * @property {boolean} disabled - Si les actions sont désactivées (enregistrement en cours)
 * @property {(field: DesignerFieldStateObj) => void} onSelect - Sélection du champ pour l'éditer
 * @property {(field: DesignerFieldStateObj) => void} onRemove - Suppression du champ
 */

/**
 * Ligne d'un champ dans le designer de formulaire
 * @param {FormDesignerFieldProps} props
 */
export default function FormDesignerField({ field, index, types, selected, disabled, onSelect, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.key, disabled });
  const type = types.find(item => item.value === field.type);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: /** @type {'relative'} */('relative'),
    zIndex: isDragging ? 1 : 'auto',
    opacity: isDragging ? 0.8 : 1,
    cursor: 'pointer'
  };

  return (
    <tr className={`table__tr${ selected ? ' table__tr--selected' : '' }`} ref={setNodeRef} style={style} onClick={() => onSelect(field)} aria-selected={selected}>
      <td className="table__td">
        <span className="material-symbols-outlined" style={{ cursor: disabled ? 'not-allowed' : 'grab', touchAction: 'none' }} aria-label={`Déplacer ${ field.label }`} {...attributes} {...listeners}>drag_indicator</span>
      </td>
      <td className="table__td">{index + 1}</td>
      <td className="table__td">
        {field.label}
        {!field.id && (
          <>
            {' '}
            <span className="badge">Nouveau</span>
          </>
        )}
      </td>
      <td className="table__td">{type ? type.name : field.type}</td>
      <td className="table__td">
        <div className="flexline flexline--align-hz flexline--gap-8">
          {field.required && type?.isInput && <span className="badge">Requis</span>}
          {!field.state && <span className="badge badge--danger">Inactif</span>}
          {field.has_other_response && type?.hasOtherResponse && <span className="badge">Autre réponse</span>}
          {!field.go_to_next_line && <span className="badge" data-tooltip="Le champ suivant s'affiche sur la même ligne">Même ligne</span>}
        </div>
      </td>
      <td className="table__td">
        <div className="flexline flexline--gap-8">
          <button type="button" className="btn btn--only-svg" data-tooltip="Supprimer" aria-label={`Supprimer ${ field.label }`} disabled={disabled} onClick={event => { event.stopPropagation(); onRemove(field); }}><span className="material-icons">delete</span></button>
        </div>
      </td>
    </tr>
  );
}
